import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Box, Typography, keyframes } from "@mui/material"
import { useFormik } from 'formik';
import { validationsSignInSchema } from "../../../validations/validationSignUpSchema";

import BaseColorText from "../../BaseColorText";
import BaseButton from "../../BaseButton";
import InputPassword from "../../InputPassword";
import BaseInput from "../../BaseInput";
import BaseLink from "../../BaseLink";
import HelpText from "../../HelpText";

import { logInWithEmailAndPassword } from "../../../api-services/auth";
import { showSuccessToast } from "../../../utils/toast";
import { setShowPreLoadingScreen, openModal } from "../../../store";
import { FIREBASE_AUTH_ERRORS } from "../../../constants/auth-errors";

const forgotPasswordHighlight = keyframes`
  0%, 75% {
    background-color: rgba(220, 53, 69, 0.15);
    font-size: 20px;
    padding: 4px 8px;
  }
  100% {
    background-color: rgba(0, 0, 0, 0);
    font-size: 16px;
    padding: 0px;
  }
`;

/**
 * @param {Object} props
 */
const SignInForm = () => {
  const dispatch = useDispatch()
  const [searchParams] = useSearchParams();
  
  const [isLoading, setIsLoading] = useState(false)
  const [message, setMessage] = useState(null)
  
  const { errors, handleBlur, handleChange, handleSubmit, touched, values } =
    useFormik({
      validateOnBlur: false,
      initialValues: {
        email: '',
        password: '',
      },
      validationSchema: validationsSignInSchema,
      onSubmit: async (values, { setSubmitting }) => {
        setIsLoading(true);
        dispatch(setShowPreLoadingScreen(true))
        
        logInWithEmailAndPassword(values.email, values.password)
        .then(async () => {
          showSuccessToast({
            message: 'Login successful!'
          })

          const redirectPath = searchParams.get('redirect') || '/';
          //store redirect 
          localStorage.setItem('redirectPath', decodeURIComponent(redirectPath))
          window.location.href = redirectPath
        })
        .catch(error => {
          setMessage(FIREBASE_AUTH_ERRORS?.[error.code] || error.message)
        })
        .finally(() => {
          setIsLoading(false)
        })
      },
    });

  const handleClickForgotPassword = () => {
    dispatch(
      openModal({
        currentModal: 'RESET_PASSWORD_MODAL',
    }))
  }

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      height: '100%'
    }}>
      <Box sx={{
        width: '100%',
        mt: '70px'
      }}>
        <Typography sx={{
          color: '#0E0F10',
          fontWeight: 700,
          fontSize: '25px'
        }}>
          Welcome to
        </Typography>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '25px',
            mt: '12px'
          }}
        >
          <BaseColorText isGradient gradientColor='#5A3BC7 30%, #5FB2EC 90%'>Physician’s portal!</BaseColorText>
        </Typography>
        <Box
          sx={{
            mt: '80px',
          }}
        >
          <form onSubmit={handleSubmit}>
            <BaseInput
              fullWidth
              label='Email'
              name='email'
              type='email'
              placeholder='Enter your email'
              value={values.email}
              onChange={handleChange("email")}
              onBlur={handleBlur("email")}
              helperText={touched.email ? errors.email : ""}
              error={touched.email && Boolean(errors.email)}
              required
            />
            <InputPassword
              sx={{
                mt: '20px',
              }}
              fullWidth
              label='Password'
              name='password'
              placeholder='Enter your password'
              value={values.password}
              onChange={handleChange}
              onBlur={handleBlur}
              helperText={touched.password ? errors.password : ""}
              error={touched.password && Boolean(errors.password)}
              required
            />
            {message && (
              <Typography variant="subText" color='primary.red'>{message}</Typography>
            )}
            <BaseButton
              loading={isLoading}
              disabled={values.email === '' || values.password === ''}
              variant='contained'
              buttonType='primary'
              type='submit'
              sx={{
                mt: { xs: '30px', sm: '50px' },
              }}
            >
              Continue
            </BaseButton>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: '16px'
              }}
            >
              <BaseLink underline={true} onClick={handleClickForgotPassword}>
                <Typography variant="subText" sx={{
                  fontSize: '16px',
                  ...(message ? {
                    animation: `${forgotPasswordHighlight} 4s ease-out forwards`,
                    borderRadius: '4px',
                  } : {}),
                }}>
                  Forgot password
                </Typography>
              </BaseLink>
            </Box>
          </form>
        </Box>
      </Box>
      <Box 
        sx={{
          mt: '80px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Typography variant="body" color="primary.darkGray">
            Don't have an account? {' '}
            <BaseLink to='/signup' underline={true}>
              Click here to sign up
            </BaseLink>
          </Typography>
        </Box>
        <HelpText
          sx={{
            borderTop: '1px solid #ABB7C0',
            textAlign: 'center',
            pt: '33px',
            pb: { xs: 0, md: '30px' },
            mt: '4px'
          }}
        >
        </HelpText>
      </Box>
    </Box>
  )
}

export default SignInForm;